import { useEffect, useMemo, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { Checkbox } from '@/components/ui/Checkbox';
import { DatePicker } from '@/components/ui/DatePicker';
import { cn } from '@/lib/utils';
import { todayIsoDate } from '@/lib/formatters';
import { useItems } from '@/features/inventory/hooks';
import { useBomGraph } from '@/features/manufacturing/boms';
import { schedulePlanLine, fmtQty } from '@/features/manufacturing/planning/engine';
import { WorkCalendar, addDays, endOfMonth } from '@/features/manufacturing/planning/dates';
import {
  PLAN_LINE_KIND_LABELS,
  useSavePlanLine,
  type PlanLineRow,
} from '@/features/manufacturing/planning/hooks';
import type { PlanLineKind, PlanningSettings } from '@/features/manufacturing/planning/types';
import { shortDate } from './shared';

interface PlanLineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  line?: PlanLineRow | null;
  settings: PlanningSettings;
  holidays: string[];
}

const KIND_HELP: Record<PlanLineKind, string> = {
  batch: 'A fixed number of units by a due date — spread evenly over the working days until then.',
  daily: 'A steady rate: this many units every working day.',
  monthly: 'What sales channels need this month — divided over the remaining working days.',
};

/**
 * Create / edit one production target. The preview under the form runs
 * the same scheduler as the planner so the operator sees the per-day
 * rate and the working days it lands on before saving.
 */
export function PlanLineDialog({ open, onOpenChange, line, settings, holidays }: PlanLineDialogProps) {
  const { data: items, isLoading: itemsLoading } = useItems();
  const { data: graph } = useBomGraph();
  const save = useSavePlanLine();
  const isEdit = !!line;

  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [itemId, setItemId] = useState<string | null>(null);
  const [kind, setKind] = useState<PlanLineKind>('daily');
  const [quantity, setQuantity] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [month, setMonth] = useState(todayIsoDate().slice(0, 7));
  const [netAgainstStock, setNetAgainstStock] = useState(false);
  const [source, setSource] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    setQuery('');
    if (line) {
      setItemId(line.item_id);
      setKind(line.kind);
      setQuantity(String(line.quantity));
      setStartDate(line.start_date ?? '');
      setEndDate(line.end_date ?? '');
      setMonth((line.start_date ?? todayIsoDate()).slice(0, 7));
      setNetAgainstStock(line.net_against_stock);
      setSource(line.source ?? '');
      setNotes(line.notes ?? '');
      setShowAll(false);
    } else {
      setItemId(null);
      setKind('daily');
      setQuantity('');
      setStartDate('');
      setEndDate('');
      setMonth(todayIsoDate().slice(0, 7));
      setNetAgainstStock(false);
      setSource('');
      setNotes('');
      setShowAll(false);
    }
  }, [open, line]);

  const buildable = useMemo(() => {
    const list = (items ?? []).filter((item) => item.is_active || item.id === itemId);
    const q = query.trim().toLowerCase();
    return list
      .filter((item) => {
        if (showAll) return true;
        const kinds = [item.kind, ...(item.additional_kinds ?? [])];
        return kinds.includes('finished_good') || (graph?.byItemId.get(item.id)?.lines.length ?? 0) > 0;
      })
      .filter((item) => !q || item.name.toLowerCase().includes(q) || item.sku.toLowerCase().includes(q))
      .slice(0, 200);
  }, [items, graph, query, showAll, itemId]);

  const selected = useMemo(() => (items ?? []).find((i) => i.id === itemId) ?? null, [items, itemId]);

  const preview = useMemo(() => {
    const qtyNum = Number(quantity);
    if (!Number.isFinite(qtyNum) || qtyNum <= 0) return null;
    const today = todayIsoDate();
    const calendar = new WorkCalendar(settings.workingDays, holidays);
    const horizonEnd = addDays(today, settings.horizonDays);
    const { schedule } = schedulePlanLine(
      {
        id: 'preview',
        itemId: itemId ?? '',
        kind,
        quantity: qtyNum,
        startDate: kind === 'monthly' ? `${month}-01` : startDate || null,
        endDate: kind === 'monthly' ? null : endDate || null,
        netAgainstStock,
        source: null,
        notes: null,
        isActive: true,
      },
      null,
      today,
      horizonEnd,
      calendar,
    );
    return schedule;
  }, [quantity, kind, startDate, endDate, month, netAgainstStock, itemId, settings, holidays]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) {
      toast.error('Pick what to build.');
      return;
    }
    const qtyNum = Number(quantity);
    if (!Number.isFinite(qtyNum) || qtyNum <= 0) {
      toast.error('Quantity must be greater than zero.');
      return;
    }
    if (kind === 'batch' && !endDate) {
      toast.error('A batch needs a due date.');
      return;
    }
    try {
      await save.mutateAsync({
        id: line?.id,
        itemId: selected.id,
        kind,
        quantity: qtyNum,
        startDate: kind === 'monthly' ? `${month}-01` : startDate || null,
        endDate: kind === 'monthly' ? null : endDate || null,
        netAgainstStock,
        source,
        notes,
        isActive: line?.is_active ?? true,
      });
      toast.success(isEdit ? 'Target updated' : `Added target for ${selected.name}`);
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save the target.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Edit production target' : 'New production target'}</DialogTitle>
          </DialogHeader>

          <div className="mt-4 grid gap-5 md:grid-cols-[1fr_1fr]">
            <div className="space-y-1.5">
              <Label>What to build</Label>
              <div className="flex items-center gap-2 rounded-md border border-border px-3 py-2">
                <Search className="h-3.5 w-3.5 text-muted-foreground" />
                <input
                  autoFocus={!isEdit}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search finished goods…"
                  className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
                />
              </div>
              <div className="max-h-56 overflow-y-auto rounded-md border border-border">
                {itemsLoading ? (
                  <div className="px-3 py-6 text-center text-xs text-muted-foreground">Loading…</div>
                ) : buildable.length === 0 ? (
                  <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                    {showAll ? 'No matching items.' : 'No finished goods or assemblies match.'}
                  </div>
                ) : (
                  buildable.map((item) => {
                    const isSelected = item.id === itemId;
                    const hasBom = (graph?.byItemId.get(item.id)?.lines.length ?? 0) > 0;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setItemId(item.id)}
                        className={cn(
                          'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-secondary/60',
                          isSelected && 'bg-secondary',
                        )}
                      >
                        <span
                          className={cn(
                            'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-border',
                            isSelected ? 'border-primary bg-primary text-primary-foreground' : 'opacity-40',
                          )}
                        >
                          {isSelected && <Check className="h-3 w-3" />}
                        </span>
                        <span className="min-w-0 flex-1 truncate">{item.name}</span>
                        <span className="shrink-0 font-mono text-[11px] text-muted-foreground">{item.sku}</span>
                        {!hasBom && (
                          <span className="shrink-0 text-[10px] uppercase tracking-wide text-fresh-salmon-dark">
                            no BOM
                          </span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
              <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                <Checkbox checked={showAll} onCheckedChange={setShowAll} ariaLabel="Show every item" />
                Show every item (e.g. to stock up on a purchased part)
              </label>
            </div>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Target type</Label>
                <div className="grid grid-cols-3 gap-1 rounded-lg bg-secondary p-1">
                  {(Object.keys(PLAN_LINE_KIND_LABELS) as PlanLineKind[]).map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setKind(k)}
                      className={cn(
                        'rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                        kind === k ? 'bg-card text-info shadow-sm' : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {PLAN_LINE_KIND_LABELS[k]}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">{KIND_HELP[kind]}</p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="plan-qty">
                  {kind === 'daily' ? 'Units per working day' : kind === 'monthly' ? 'Units this month' : 'Units to build'}
                </Label>
                <Input
                  id="plan-qty"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="0"
                  inputMode="decimal"
                  className="tabular-nums"
                />
              </div>

              {kind === 'monthly' ? (
                <div className="space-y-1.5">
                  <Label htmlFor="plan-month">Month</Label>
                  <Input
                    id="plan-month"
                    type="month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value || todayIsoDate().slice(0, 7))}
                  />
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="plan-start">{kind === 'batch' ? 'Start (optional)' : 'From (optional)'}</Label>
                    <DatePicker id="plan-start" value={startDate} onChange={setStartDate} placeholder="Today" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="plan-end">{kind === 'batch' ? 'Due date' : 'Until (optional)'}</Label>
                    <DatePicker
                      id="plan-end"
                      value={endDate}
                      onChange={setEndDate}
                      placeholder={kind === 'batch' ? 'Pick a date' : 'End of horizon'}
                    />
                  </div>
                </div>
              )}

              <label className="flex cursor-pointer items-start gap-2 text-sm">
                <Checkbox
                  checked={netAgainstStock}
                  onCheckedChange={setNetAgainstStock}
                  ariaLabel="Subtract finished goods on hand"
                  className="mt-0.5"
                />
                <span>
                  Subtract finished goods already on hand
                  <span className="block text-xs text-muted-foreground">
                    Off: build exactly this many. On: this is what is needed, so stock counts first.
                  </span>
                </span>
              </label>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="plan-source">Source</Label>
                  <Input
                    id="plan-source"
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    placeholder="Amazon forecast, dealer PO…"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="plan-notes">Notes</Label>
                  <Textarea id="plan-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={1} />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-5 rounded-lg border border-border bg-secondary/40 px-4 py-3 text-sm">
            {!preview ? (
              <span className="text-muted-foreground">Enter a quantity to preview the schedule.</span>
            ) : preview.issues.length > 0 ? (
              <span className="text-fresh-salmon-dark">
                {preview.issues.map((i) => i[0].toUpperCase() + i.slice(1)).join('. ')}.
              </span>
            ) : (
              <span>
                <span className="font-medium tabular-nums">{fmtQty(preview.perDay)}</span>
                <span className="text-muted-foreground"> per working day · </span>
                <span className="tabular-nums">{preview.workingDays}</span>
                <span className="text-muted-foreground"> working days, </span>
                {shortDate(preview.firstDate)}
                <span className="text-muted-foreground"> → </span>
                {shortDate(preview.lastDate)}
                {kind === 'monthly' && (
                  <span className="text-muted-foreground"> (month ends {shortDate(endOfMonth(`${month}-01`))})</span>
                )}
                {preview.totalBeyondHorizon > 0 && (
                  <span className="text-muted-foreground">
                    {' '}
                    · {fmtQty(preview.totalBeyondHorizon)} fall past the {settings.horizonDays}-day horizon
                  </span>
                )}
              </span>
            )}
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending || !selected}>
              {save.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add target'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
